import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { useOnboardingChecklist } from "@/hooks/use-onboarding-checklist";

interface OnboardingChecklistProps {
  projectId: string;
  monitorCount: number;
}

export function OnboardingChecklist({
  projectId,
  monitorCount,
}: OnboardingChecklistProps) {
  const navigate = useNavigate();
  const { items, completedCount, totalCount, dismissed, dismiss } =
    useOnboardingChecklist(projectId, monitorCount);
  const [open, setOpen] = useState(true);

  if (dismissed || completedCount === totalCount) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarGroupLabel className="flex items-center justify-between">
          <CollapsibleTrigger className="flex flex-1 items-center gap-1">
            <span>Getting Started</span>
            <span className="ml-1 text-xs text-muted-foreground">
              {completedCount}/{totalCount}
            </span>
            <ChevronDown
              className={cn(
                "ml-auto size-3.5 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
          </CollapsibleTrigger>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
            }}
            className="ml-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton
                    onClick={() => {
                      if (!item.completed) navigate(item.href);
                    }}
                    className={cn(
                      "gap-2",
                      item.completed &&
                        "text-muted-foreground line-through pointer-events-none",
                    )}
                  >
                    {item.completed ? (
                      <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
                    ) : (
                      <Circle className="size-4 shrink-0" />
                    )}
                    <span className="text-xs">{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
